import { ROTransaction, RWTransaction } from '../../../db'
import { Context, Id, Order, RealDateTimeProvider } from '../../../common'
import { Citizen, citizenCategoryShorthand } from '../../citizens'
import { padStart } from 'lodash'

export enum CardStatus {
  NEW = 'new',
  ACTIVE = 'active',
  COMPLETED = 'completed',
}

export function cardStatusName(status?: CardStatus): string {
  switch (status) {
    case CardStatus.NEW:
      return 'Новая'

    case CardStatus.ACTIVE:
      return 'Активна'

    case CardStatus.COMPLETED:
      return 'Завершена'

    default:
      return '-'
  }
}

export function createCardNumber(citizen: Citizen, createdAt: Date, meIndex: number, cipNumber: number): string {
  const category = citizenCategoryShorthand(citizen.category)
  const regionCode = '61'
  const municipality = padStart(meIndex.toString(), 3, '0')
  const issuePoint = padStart(cipNumber.toString(), 3, '0')
  const issueDate =
    `${createdAt.getUTCFullYear()}` +
    `${padStart((createdAt.getUTCMonth() + 1).toString(), 2, '0')}` +
    `${padStart(createdAt.getUTCDate().toString(), 2, '0')}`
  const issueTime =
    `${padStart(createdAt.getUTCHours().toString(), 2, '0')}` +
    `${padStart(createdAt.getUTCMinutes().toString(), 2, '0')}` +
    `${padStart(createdAt.getUTCMilliseconds().toString(), 3, '0')}`
  return `${category}_${regionCode}.${municipality}.${issuePoint}.${issueDate}.${issueTime}`
}

export interface IdentityCard {
  id: Id
  month: number
  year: number
  cardIssuePointId?: Id
  citizenId: Id
  createdAt: Date
  confirmedAt?: Date
}

export interface ConfirmableDataForIdentityCard {
  month: number
  year: number
  cardIssuePointId: Id
  citizenId: Id
}

export interface PrintableDataForIdentityCard {
  month: number
  year: number
  cardIssuePointId?: Id
  citizenId: Id
}

export interface IdentityCardFilter {
  month?: number
  year?: number
  citizenIds?: Id[]
  cardIssuePointId?: Id
  dateFrom?: Date
  dateTo?: Date
  onlyConfirmed?: boolean
}

export enum IdentityCardOrderFields {
  CREATED_AT = 'createdAt',
}

export interface IdentityCardFindOptions {
  filter?: IdentityCardFilter
  order?: Order<IdentityCardOrderFields> | undefined
}

export function getIdentityCardStatus(card: IdentityCard): CardStatus {
  const now = new RealDateTimeProvider().get().getTime()
  const cardStart = new Date(card.year, card.month, 1).getTime()
  const cardEnd = new Date(card.year, card.month + 1, 1).getTime() - 1

  if (cardEnd < now) {
    // карта истекла до текущего момента
    return CardStatus.COMPLETED
  } else if (cardEnd > now && cardStart < now) {
    // карта актуальна, проверям выдачу
    return card.confirmedAt ? CardStatus.ACTIVE : CardStatus.NEW
  } else {
    // карта будет актуальна потом
    return CardStatus.NEW
  }
}

export function getCurrentCardStatus(cards?: IdentityCard[]): CardStatus | undefined {
  if (!cards || !cards.length) return

  const now = new RealDateTimeProvider().get()
  const card = cards.find(c => c.year === now.getFullYear() && c.month === now.getMonth())
  if (card) return getIdentityCardStatus(card)
  return
}

export interface IdentityCardsService {
  confirm(ctx: Context, card: ConfirmableDataForIdentityCard): Promise<IdentityCard>
  print(ctx: Context, card: PrintableDataForIdentityCard): Promise<IdentityCard>
  findCitizenCards(ctx: Context, citizenId: Id): Promise<IdentityCard[]>
}

export interface IdentityCardsRepository {
  findById(tx: ROTransaction, id: Id): Promise<IdentityCard | undefined>
  findAll(tx: ROTransaction, opts?: IdentityCardFindOptions): Promise<IdentityCard[]>
  findByDateAndCitizen(tx: ROTransaction, month: number, year: number, citizenId: Id): Promise<IdentityCard | undefined>
  save(tx: RWTransaction, identityCard: IdentityCard): Promise<void>
}
